import { useRef, useState, useEffect } from "react";
import { Link, useParams } from "wouter";
import { useModals } from "../components/common/ModalsProvider.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { abrirArquivo } from "../lib/open-file.js";

/**
 * F-proforma-attach-invoice — botão de anexar PDF da invoice retornada,
 * usável DEPOIS de registrada (o fluxo de "Registrar invoice" só anexa no
 * momento da transição). POST /uploads relativo (proxy Vite / mesmo-origin).
 */
function ReturnedInvoiceAttach({
  companyId,
  proformaRequestId,
  onUploaded,
}: {
  companyId: number;
  proformaRequestId: number;
  onUploaded: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modals = useModals();
  const t = useT();
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded border border-neutral-700 px-2 py-1 text-xs text-neutral-300 hover:border-neutral-500 disabled:opacity-40"
      >
        {busy ? t("proformaDetail.uploading") : t("proformaDetail.attachPdf")}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("companyId", String(companyId));
            fd.append("ownerId", String(proformaRequestId));
            fd.append("ownerKind", "proforma_returned_invoice");
            const resp = await fetch(`/uploads`, {
              method: "POST",
              credentials: "include",
              body: fd,
            });
            if (!resp.ok) {
              const msg = await resp.text();
              void modals.alert({
                title: t("proformaDetail.error"),
                message: `${t("proformaDetail.uploadFailed")} (${resp.status}): ${msg}`,
                tone: "danger",
              });
            } else {
              onUploaded();
            }
          } catch (err) {
            void modals.alert({
              title: t("proformaDetail.error"),
              message: `${t("proformaDetail.uploadFailed")}: ${err instanceof Error ? err.message : String(err)}`,
              tone: "danger",
            });
          } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
          }
        }}
      />
    </>
  );
}

type ProformaStatus =
  | "draft"
  | "sent"
  | "approved"
  | "rejected"
  | "invoice_received"
  | "paid"
  | "closed"
  | "cancelled";

type TransitionAction =
  | "send"
  | "approve"
  | "reject"
  | "reopen"
  | "receive_invoice"
  | "mark_paid"
  | "close"
  | "cancel";

interface TransitionMeta {
  action: TransitionAction;
  label: string;
  requiresReason?: boolean;
  requiresAdmin?: boolean;
  tone?: "primary" | "danger" | "neutral";
}

// Espelho local do state-machine do backend (packages/domain/src/proforma).
// Duplicação consciente: evita import direto da package domain no web,
// que não está exposto via tRPC. Quando divergir, ajustar nos 2 lados.
function buildTransitions(t: ReturnType<typeof useT>): Record<ProformaStatus, TransitionMeta[]> {
  return {
    draft: [
      { action: "send", label: t("proformaDetail.transSend"), tone: "primary" },
      {
        action: "cancel",
        label: t("proformaDetail.transCancel"),
        requiresReason: true,
        tone: "danger",
      },
    ],
    sent: [
      { action: "approve", label: t("proformaDetail.transApprove"), tone: "primary" },
      {
        action: "reject",
        label: t("proformaDetail.transReject"),
        requiresReason: true,
        tone: "danger",
      },
      {
        action: "cancel",
        label: t("proformaDetail.transCancelAdmin"),
        requiresReason: true,
        requiresAdmin: true,
        tone: "danger",
      },
    ],
    approved: [
      {
        action: "receive_invoice",
        label: t("proformaDetail.transReceiveInvoice"),
        tone: "primary",
      },
      {
        action: "cancel",
        label: t("proformaDetail.transCancelAdmin"),
        requiresReason: true,
        requiresAdmin: true,
        tone: "danger",
      },
    ],
    rejected: [
      { action: "reopen", label: t("proformaDetail.transReopen"), tone: "primary" },
      {
        action: "cancel",
        label: t("proformaDetail.transCancel"),
        requiresReason: true,
        tone: "danger",
      },
    ],
    invoice_received: [
      { action: "mark_paid", label: t("proformaDetail.transMarkPaid"), tone: "primary" },
      {
        action: "cancel",
        label: t("proformaDetail.transCancelAdmin"),
        requiresReason: true,
        requiresAdmin: true,
        tone: "danger",
      },
    ],
    paid: [{ action: "close", label: t("proformaDetail.transClose"), tone: "primary" }],
    closed: [],
    cancelled: [],
  };
}

const STATUS_TONE: Record<ProformaStatus, string> = {
  draft: "bg-neutral-700/40 text-neutral-300",
  sent: "bg-sky-500/15 text-sky-300",
  approved: "bg-lime/15 text-lime",
  rejected: "bg-amber-500/15 text-amber-300",
  invoice_received: "bg-violet-500/15 text-violet-300",
  paid: "bg-lime/20 text-lime",
  closed: "bg-neutral-800 text-neutral-400",
  cancelled: "bg-red-500/15 text-red-300",
};

function buildStatusLabels(t: ReturnType<typeof useT>): Record<ProformaStatus, string> {
  return {
    draft: t("proformaDetail.statusDraft"),
    sent: t("proformaDetail.statusSent"),
    approved: t("proformaDetail.statusApproved"),
    rejected: t("proformaDetail.statusRejected"),
    invoice_received: t("proformaDetail.statusInvoiceReceived"),
    paid: t("proformaDetail.statusPaid"),
    closed: t("proformaDetail.statusClosed"),
    cancelled: t("proformaDetail.statusCancelled"),
  };
}

export default function ProformaDetail() {
  const params = useParams<{ id: string }>();
  const proformaRequestId = Number(params.id);
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const tr = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [tab, setTab] = useState<"items" | "others" | "audit">("items");
  const [showAddItem, setShowAddItem] = useState(false);
  const [transition, setTransition] = useState<TransitionMeta | null>(null);

  // Probe paralelo: tenta carregar a proforma em cada empresa acessível.
  const probeQueries = trpc.useQueries((t) =>
    accessibleCompanies.map((c) =>
      t.proformaRequests.getById(
        { companyId: c.id, proformaRequestId },
        { retry: false, refetchOnMount: false, enabled: !Number.isNaN(proformaRequestId) },
      ),
    ),
  );
  const probeIndex = probeQueries.findIndex((q) => q.data != null);
  const companyId = probeIndex >= 0 ? (accessibleCompanies[probeIndex]?.id ?? null) : null;
  const detail = probeIndex >= 0 ? probeQueries[probeIndex]?.data : null;
  const loading = probeQueries.some((q) => q.isLoading);

  // F-proforma-detail-blank-page (2026-05-27) — Rules of Hooks: useUtils e
  // useMutation precisam ficar ANTES dos early returns para manter a ordem
  // de hooks estável entre renders. Quando estavam após `if (!detail) return`,
  // o primeiro render (loading) pulava esses hooks; o segundo (data) chamava
  // → React detectava mudança de ordem → tree desmontado → página em branco.
  const utils = trpc.useUtils();
  const resendEmailMutation = trpc.proformaRequests.sendEmail.useMutation({
    onSuccess: (res) => {
      utils.proformaRequests.invalidate();
      void modals.alert({
        message: `${tr("proformaDetail.emailResent")} — ${tr("proformaDetail.resendSentToPre")} ${res.sentTo}`,
      });
    },
    onError: (e) => {
      void modals.alert({
        title: tr("proformaDetail.error"),
        message: `${tr("proformaDetail.emailResendFailed")}: ${e.message}`,
        tone: "danger",
      });
    },
  });
  // F-proforma-pdf-download (CEO 2026-09-30) — baixar o PDF da proforma: abre
  // o guardado no envio; sem PDF ainda (rascunho), gera na hora.
  const generatePdfMutation = trpc.proformaRequests.generatePdf.useMutation();
  async function abrirPdf(pdfAttachmentId: number | null, statusAtual: string) {
    if (companyId == null) return;
    // Abre a aba já no clique (senão o navegador bloqueia o pop-up depois do await).
    const win = window.open("about:blank", "_blank");
    try {
      // Enquanto a proforma ainda muda (até ser paga), regera: o guardado pode
      // ser de antes de uma edição (caso do VIN na PRF-2026-0023).
      const regera = ["draft", "sent", "approved", "invoice_received"].includes(statusAtual);
      const attachmentId =
        pdfAttachmentId != null && !regera
          ? pdfAttachmentId
          : (await generatePdfMutation.mutateAsync({ companyId, proformaRequestId })).attachmentId;
      const res = await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId });
      if (win && res?.url) win.location.href = res.url;
      else if (res?.url) window.open(res.url, "_blank", "noopener");
      if (pdfAttachmentId == null) void utils.proformaRequests.getById.invalidate();
    } catch (e) {
      win?.close();
      void modals.alert({
        title: tr("proformaDetail.error"),
        message: `${tr("proformaDetail.pdfErrorPre")}${e instanceof Error ? e.message : String(e)}`,
        tone: "danger",
      });
    }
  }
  // F-proforma-attach-invoice — anexos da invoice retornada (PDF do fornecedor).
  const returnedInvoiceAttachments = trpc.attachments.listByOwner.useQuery(
    {
      companyId: companyId ?? 0,
      ownerKind: "proforma_returned_invoice",
      ownerId: proformaRequestId,
    },
    { enabled: companyId !== null && !Number.isNaN(proformaRequestId) },
  );
  // F-bs-mirror — estado/ação de espelhamento p/ a empresa destino (BS).
  const mirrorStatusQuery = trpc.proformaRequests.mirrorStatus.useQuery(
    { companyId: companyId ?? 0, proformaRequestId },
    { enabled: companyId !== null && !Number.isNaN(proformaRequestId) },
  );
  const mirrorValidationQuery = trpc.proformaRequests.mirrorValidation.useQuery(
    { companyId: companyId ?? 0, proformaRequestId },
    { enabled: companyId !== null && !Number.isNaN(proformaRequestId), retry: false },
  );
  const importToRecipientMut = trpc.proformaRequests.importToRecipient.useMutation({
    onSuccess: (res) => {
      void mirrorStatusQuery.refetch();
      void mirrorValidationQuery.refetch();
      const created = res.created.length;
      const skipped = res.skippedExisting.length;
      const warnings = res.warnings ?? [];
      const base =
        created > 0
          ? `${created} ${tr("proformaDetail.dossiersCreatedSuffix")}${skipped ? ` ${skipped} ${tr("proformaDetail.alreadyExistedSuffix")}` : ""}`
          : tr("proformaDetail.allAlreadyImported");
      void modals.alert({
        title: warnings.length > 0 ? tr("proformaDetail.importedWithWarnings") : undefined,
        tone: warnings.length > 0 ? "warning" : undefined,
        message:
          warnings.length > 0
            ? `${base}\n\n⚠️ ${warnings.length} ${tr("proformaDetail.linesWithoutCommission")}\n${warnings.map((w) => `• ${w}`).join("\n")}`
            : base,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: tr("proformaDetail.error"),
        message: `${tr("proformaDetail.importFailed")}: ${e.message}`,
        tone: "danger",
      }),
  });

  if (Number.isNaN(proformaRequestId)) {
    return (
      <Shell>
        <p className="text-sm text-red-400">{tr("proformaDetail.invalidId")}</p>
      </Shell>
    );
  }
  if (!user) return null;
  if (loading && !detail) {
    return (
      <Shell>
        <p className="text-sm text-neutral-500">{tr("proformaDetail.loading")}</p>
      </Shell>
    );
  }
  if (!detail || companyId === null) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{tr("proformaDetail.notFound")}</p>
      </Shell>
    );
  }

  const { header, items, otherServices } = detail;
  const status = header.status as ProformaStatus;
  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");
  const availableTransitions = buildTransitions(tr)[status];
  const statusLabels = buildStatusLabels(tr);

  // F-proforma-email-snapshot — sem e-mail gravado o botão também aparece: o
  // operador digita o destinatário (antes ficava sem nenhum jeito de enviar).
  // CEO 2026-10-26 — pagamento registrado POR FORA (série INT): registro
  // interno, sem transições, e-mail nem PDF; o admin pode desfazer.
  const porFora = header.externalPaymentMethod != null;
  const canResendEmail =
    !porFora && (status === "sent" || status === "approved" || status === "invoice_received");

  // F-bs-mirror — botão "Importar pra <empresa destino>" em proforma
  // intercompany paga/fechada. importRemaining = itens ainda não espelhados.
  const mirror = mirrorStatusQuery.data;
  const canImport = Boolean(mirror?.eligible);
  const importRemaining = mirror ? mirror.total - mirror.mirrored : 0;

  return (
    <Shell>
      <header className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-semibold font-mono">{header.reference}</h2>
            <p className="text-sm text-neutral-400">
              {tr("proformaDetail.recipientPrefix")}: {header.recipientName} ({header.recipientType}
              ){header.recipientEmail && ` · ${header.recipientEmail}`}
            </p>
          </div>
          <span className={`inline-block rounded px-3 py-1 text-sm ${STATUS_TONE[status]}`}>
            {statusLabels[status]}
          </span>
        </div>
        {porFora && (
          <ExternalPaymentInfo
            companyId={companyId}
            proformaRequestId={proformaRequestId}
            method={header.externalPaymentMethod!}
            note={header.externalPaymentNote}
            paidAt={header.paidAt}
            cancelled={status === "cancelled"}
            cancelledReason={header.cancelledReason}
            isAdmin={isAdmin}
          />
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Kpi
            label={tr("proformaDetail.kpiGross")}
            value={formatMoney(header.grossTotal, { decimals: 2 })}
          />
          <Kpi
            label={tr("proformaDetail.kpiRetention")}
            value={formatMoney(header.retentionTotal, { decimals: 2 })}
          />
          <Kpi
            label={tr("proformaDetail.kpiNet")}
            value={formatMoney(header.netTotal, { decimals: 2 })}
            highlight={Number(header.advancesDeducted ?? 0) <= 0}
          />
          {/* F-adiantamento-tecnico — a fatura do técnico veio com o desconto
              dos adiantamentos: o que a empresa paga é líquido − adiantamentos. */}
          {Number(header.advancesDeducted ?? 0) > 0 && (
            <Kpi
              label={tr("proformaDetail.kpiToPay")}
              value={
                <span data-testid="kpi-to-pay">
                  {formatMoney(Number(header.netTotal) - Number(header.advancesDeducted), {
                    decimals: 2,
                  })}
                  <span className="ml-1 text-[11px] font-normal text-amber-300">
                    (− {formatMoney(header.advancesDeducted, { decimals: 2 })}{" "}
                    {tr("proformaDetail.kpiAdvancesShort")})
                  </span>
                </span>
              }
              highlight
            />
          )}
          <Kpi
            label={tr("proformaDetail.kpiPeriod")}
            value={
              header.periodStart && header.periodEnd
                ? `${header.periodStart} → ${header.periodEnd}`
                : "—"
            }
          />
        </div>

        {/* F-proforma-margem (CEO 2026-09-28) — conferência interna antes de
            enviar: faturado ao cliente × pago ao técnico = margem da empresa.
            Não vai no PDF nem no email do técnico. */}
        {items.length > 0 &&
          (() => {
            const fat = items.reduce((s, it) => s + Number(it.faturadoCliente ?? 0), 0);
            const pago = Number(header.netTotal);
            const margem = fat - pago;
            const pct = fat > 0 ? (margem / fat) * 100 : null;
            const alerta = fat > 0 && margem <= 0.01;
            return (
              <div
                className="rounded-md border border-neutral-800 bg-neutral-950/40 px-4 py-3"
                data-testid="proforma-margem"
              >
                <div className="mb-2 text-[10px] uppercase tracking-wider text-neutral-500">
                  {tr("proformaDetail.marginTitle")}
                </div>
                <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
                  <Kpi
                    label={tr("proformaDetail.marginInvoiced")}
                    value={formatMoney(fat, { decimals: 2 })}
                  />
                  <Kpi
                    label={`− ${tr("proformaDetail.marginPaid")}`}
                    value={formatMoney(pago, { decimals: 2 })}
                  />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-neutral-500">
                      = {tr("proformaDetail.marginGross")}
                    </div>
                    <div
                      className="font-mono text-lg font-semibold"
                      style={{ color: alerta ? "#DC2626" : "#16A34A" }}
                    >
                      {formatMoney(margem, { decimals: 2 })}
                      {pct != null && (
                        <span className="ml-2 text-sm font-normal">
                          ({pct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {alerta && (
                  <p className="mt-2 text-xs font-medium" style={{ color: "#DC2626" }}>
                    ⚠ {tr("proformaDetail.marginAlert")}
                  </p>
                )}
                <p className="mt-1 text-[11px] text-neutral-500">
                  {tr("proformaDetail.marginHint")}
                </p>
              </div>
            );
          })()}

        {/* F-proforma-attach-invoice — invoice retornada do fornecedor:
            número + anexos (PDF). Só aparece quando registrada. */}
        {(header.returnedInvoiceNumber ||
          header.returnedInvoiceId ||
          (returnedInvoiceAttachments.data?.length ?? 0) > 0) && (
          <div className="rounded-md border border-neutral-800 bg-neutral-950/40 p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {tr("proformaDetail.returnedInvoice")} ({header.returnedInvoiceKind ?? "—"})
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-neutral-200">
                {header.returnedInvoiceNumber ??
                  (header.returnedInvoiceId ? `#${header.returnedInvoiceId}` : "—")}
              </span>
              {(returnedInvoiceAttachments.data ?? []).map((a) => (
                <button
                  key={a.id}
                  onClick={() =>
                    void abrirArquivo(
                      async () =>
                        (
                          await utils.attachments.getDownloadUrl.fetch({
                            companyId: companyId,
                            attachmentId: a.id,
                          })
                        )?.url,
                    )
                  }
                  className="inline-flex items-center gap-1 rounded border border-sky-500/40 hover:border-sky-500 text-sky-300 px-2 py-1 text-xs"
                  title={a.originalFilename}
                >
                  📎 {a.originalFilename}
                </button>
              ))}
              {(returnedInvoiceAttachments.data?.length ?? 0) === 0 && (
                <span className="text-xs text-neutral-500">
                  {tr("proformaDetail.noPdfAttached")}
                </span>
              )}
              {canEdit && (
                <ReturnedInvoiceAttach
                  companyId={companyId}
                  proformaRequestId={proformaRequestId}
                  onUploaded={() => void returnedInvoiceAttachments.refetch()}
                />
              )}
            </div>
          </div>
        )}

        {/* F-roles — toda a barra de transições/ações de escrita escondida p/
            visualizador (somente leitura). Downloads de anexos acima permanecem. */}
        {canEdit &&
          !porFora &&
          (availableTransitions.length > 0 || canResendEmail || canImport) && (
            <div className="flex flex-wrap gap-2 border-t border-neutral-800 pt-3">
              {availableTransitions.map((t) => {
                const disabled = t.requiresAdmin && !isAdmin;
                return (
                  <button
                    key={t.action}
                    onClick={() => setTransition(t)}
                    disabled={disabled}
                    title={disabled ? tr("proformaDetail.requiresAdminRole") : undefined}
                    className={
                      t.tone === "primary"
                        ? "rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-3 py-1.5 text-sm font-medium"
                        : t.tone === "danger"
                          ? "rounded-md border border-red-500/40 hover:border-red-500 text-red-300 disabled:opacity-40 px-3 py-1.5 text-sm"
                          : "rounded-md border border-neutral-800 hover:border-neutral-700 disabled:opacity-40 px-3 py-1.5 text-sm"
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
              {items.length > 0 && (
                <button
                  onClick={() => void abrirPdf(header.pdfAttachmentId ?? null, status)}
                  disabled={generatePdfMutation.isPending}
                  className="rounded-md border border-neutral-800 hover:border-neutral-700 disabled:opacity-40 px-3 py-1.5 text-sm"
                  title={tr("proformaDetail.pdfBtnTitle")}
                >
                  {generatePdfMutation.isPending
                    ? tr("proformaDetail.pdfGenerating")
                    : tr("proformaDetail.pdfBtn")}
                </button>
              )}
              {canResendEmail && (
                <button
                  onClick={async () => {
                    // F-proforma-email-override — destinatário editável no reenvio.
                    // Pré-preenche com o cadastrado; se o operador mudar, vale só
                    // para este envio (não altera a proforma nem o PDF).
                    const typed = await modals.promptReason({
                      title: tr("proformaDetail.resendPromptTitle"),
                      message: tr("proformaDetail.resendPromptMessage"),
                      placeholder: tr("proformaDetail.resendPromptPlaceholder"),
                      defaultValue: header.recipientEmail ?? detail.recipientEmailSuggested ?? "",
                      confirmLabel: tr("proformaDetail.resendSendBtn"),
                      maxLength: 320,
                      required: true,
                    });
                    if (typed == null) return;
                    const email = typed.trim();
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                      await modals.alert({ message: tr("proformaDetail.resendInvalidEmail") });
                      return;
                    }
                    resendEmailMutation.mutate({
                      companyId,
                      proformaRequestId,
                      // Só manda override se realmente mudou (mantém o audit honesto).
                      ...(email.toLowerCase() !== (header.recipientEmail ?? "").toLowerCase()
                        ? { recipientEmail: email }
                        : {}),
                    });
                  }}
                  disabled={resendEmailMutation.isPending}
                  className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 disabled:opacity-40 px-3 py-1.5 text-sm"
                  title={`${tr("proformaDetail.resendTo")} ${header.recipientEmail}`}
                >
                  {resendEmailMutation.isPending
                    ? tr("proformaDetail.sending")
                    : tr("proformaDetail.resendEmail")}
                </button>
              )}
              {canImport && (
                <button
                  onClick={async () => {
                    if (importRemaining <= 0) return;
                    if (
                      await modals.confirm({
                        title: tr("proformaDetail.confirm"),
                        message:
                          `${tr("proformaDetail.importConfirmPre")} ${importRemaining} ${tr("proformaDetail.importConfirmMid")} ${header.recipientName}? ` +
                          tr("proformaDetail.importConfirmSuffix"),
                      })
                    ) {
                      importToRecipientMut.mutate({ companyId, proformaRequestId });
                    }
                  }}
                  disabled={importToRecipientMut.isPending || importRemaining <= 0}
                  className="rounded-md border border-lime/40 hover:border-lime text-lime disabled:opacity-40 px-3 py-1.5 text-sm"
                  title={tr("proformaDetail.importBtnTitle")}
                >
                  {importToRecipientMut.isPending
                    ? tr("proformaDetail.importing")
                    : importRemaining <= 0
                      ? tr("proformaDetail.alreadyImported")
                      : `📥 ${tr("proformaDetail.importTo")} ${header.recipientName}${
                          mirror && mirror.mirrored > 0
                            ? ` (${tr("proformaDetail.remainingPre")} ${importRemaining})`
                            : ""
                        }`}
                </button>
              )}
              {/* F-bs-mirror-navfix (2026-05-31) — Numa proforma intercompany ainda
                em DRAFT, o espelhamento não é elegível (só após 'enviada'). Em vez
                de esconder a opção (o que confundia: "o botão sumiu"), mostramos
                a ação visível porém desabilitada, com a instrução do próximo passo. */}
              {!canImport && status === "draft" && mirror?.recipientCompanyId != null && (
                <span
                  className="rounded-md border border-dashed border-neutral-700 text-neutral-500 px-3 py-1.5 text-sm cursor-help"
                  title={tr("proformaDetail.intercompanyHint")}
                >
                  📥 {tr("proformaDetail.importTo")} {header.recipientName} —{" "}
                  {tr("proformaDetail.sendFirst")}
                </span>
              )}
            </div>
          )}
      </header>

      {mirror?.eligible &&
        mirrorValidationQuery.data &&
        mirrorValidationQuery.data.total > 0 &&
        (() => {
          const v = mirrorValidationQuery.data;
          const allOk = v.matched === v.total && Math.abs(v.expectedTotal - v.mirroredTotal) < 0.01;
          return (
            <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm font-medium text-neutral-300">
                  {tr("proformaDetail.mirrorCheckPre")} {header.recipientName}
                </span>
                <span className={`text-xs font-medium ${allOk ? "text-lime" : "text-amber-300"}`}>
                  {allOk
                    ? tr("proformaDetail.mirrorAllOk")
                    : `⚠️ ${v.matched}/${v.total} ${tr("proformaDetail.mirroredCount")}`}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                {tr("proformaDetail.expectedProforma")}:{" "}
                {formatMoney(v.expectedTotal, { decimals: 2 })} · {tr("proformaDetail.mirroredBs")}:{" "}
                {formatMoney(v.mirroredTotal, { decimals: 2 })}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-neutral-500 border-b border-neutral-800">
                    <tr>
                      <th className="px-2 py-1">{tr("proformaDetail.colPlate")}</th>
                      <th className="px-2 py-1">{tr("proformaDetail.colTechnician")}</th>
                      <th className="px-2 py-1 text-right">{tr("proformaDetail.colExpected")}</th>
                      <th className="px-2 py-1 text-right">{tr("proformaDetail.colInBs")}</th>
                      <th className="px-2 py-1">{tr("proformaDetail.colDossierBs")}</th>
                      <th className="px-2 py-1 text-center">{tr("proformaDetail.colOk")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.items.map((it) => (
                      <tr key={it.itemId} className="border-b border-neutral-900">
                        <td className="px-2 py-1 tabular-nums">{it.plate ?? "—"}</td>
                        <td className="px-2 py-1 text-neutral-400">{it.technicianName ?? "—"}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {formatMoney(it.expected, { decimals: 2 })}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {it.bsFaturado == null
                            ? "—"
                            : formatMoney(it.bsFaturado, { decimals: 2 })}
                        </td>
                        <td className="px-2 py-1 text-neutral-400">
                          {it.bsDossierCode ?? "—"}
                          {it.matchedBy === "plate" ? ` ${tr("proformaDetail.byPlate")}` : ""}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {it.bsDossierCode == null ? (
                            <span className="text-red-400">{tr("proformaDetail.missing")}</span>
                          ) : it.valueOk ? (
                            <span className="text-lime">✓</span>
                          ) : (
                            <span className="text-amber-300">
                              {tr("proformaDetail.valueMismatch")}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })()}

      <div className="border-b border-neutral-800 flex gap-1 text-sm">
        <TabBtn active={tab === "items"} onClick={() => setTab("items")}>
          {tr("proformaDetail.tabItems")} ({items.length})
        </TabBtn>
        <TabBtn active={tab === "others"} onClick={() => setTab("others")}>
          {tr("proformaDetail.tabOtherServices")} ({otherServices.length})
        </TabBtn>
        <TabBtn active={tab === "audit"} onClick={() => setTab("audit")}>
          {tr("proformaDetail.tabAudit")}
        </TabBtn>
      </div>

      {tab === "items" && (
        <ItemsTab
          companyId={companyId}
          proformaRequestId={proformaRequestId}
          items={items}
          isDraft={status === "draft"}
          canRefresh={
            (status === "draft" || status === "sent" || status === "approved") &&
            header.returnedInvoiceId == null &&
            header.returnedInvoiceNumber == null
          }
          canEdit={canEdit}
          onOpenAdd={() => setShowAddItem(true)}
        />
      )}
      {tab === "others" && <OtherServicesTab otherServices={otherServices} />}
      {tab === "audit" && (
        <AuditTab companyId={companyId} proformaRequestId={proformaRequestId} isAdmin={isAdmin} />
      )}

      {canEdit && showAddItem && (
        <AddItemModal
          companyId={companyId}
          proformaRequestId={proformaRequestId}
          onClose={() => setShowAddItem(false)}
        />
      )}

      {canEdit && transition && (
        <TransitionModal
          companyId={companyId}
          proformaRequestId={proformaRequestId}
          transition={transition}
          recipientEmail={header.recipientEmail ?? detail.recipientEmailSuggested ?? null}
          recipientName={header.recipientName}
          recipientType={header.recipientType}
          recipientTechnicianId={header.recipientTechnicianId ?? null}
          netTotal={Number(header.netTotal)}
          ownMirrors={mirror?.ownMirrors ?? null}
          onClose={() => setTransition(null)}
        />
      )}
    </Shell>
  );
}

function Kpi({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`font-mono ${highlight ? "text-lime text-lg" : "text-sm"}`}>{value}</p>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 border-b-2 -mb-px transition-colors ${
        active
          ? "border-lime text-lime"
          : "border-transparent text-neutral-400 hover:text-neutral-200"
      }`}
    >
      {children}
    </button>
  );
}

interface ProformaItem {
  id: number;
  vehicleServiceJobId: number;
  dossierCode: string | null;
  invoiceNumberExternal: string | null;
  technicianNameSnapshot: string;
  customerNameSnapshot: string | null;
  vehiclePlateSnapshot: string | null;
  vehicleMakeModelSnapshot: string | null;
  serviceDateSnapshot: string | null;
  grossAmountSnapshot: string;
  retentionPercentSnapshot: string;
  retentionAmountSnapshot: string;
  netAmountSnapshot: string;
  technicianAmountSnapshot?: string | null;
  intermediaryAmountSnapshot?: string | null;
  faturadoCliente?: number;
}

function ItemsTab({
  companyId,
  proformaRequestId,
  items,
  isDraft,
  canRefresh,
  canEdit,
  onOpenAdd,
}: {
  companyId: number;
  proformaRequestId: number;
  items: ProformaItem[];
  isDraft: boolean;
  canRefresh: boolean;
  canEdit: boolean;
  onOpenAdd: () => void;
}) {
  const utils = trpc.useUtils();
  const modals = useModals();
  const t = useT();
  // F-proforma-refresh-items — rascunho com itens velhos (metade marcada depois).
  const refreshMutation = trpc.proformaRequests.refreshItems.useMutation({
    onSuccess: (r) => {
      void utils.proformaRequests.getById.invalidate();
      void modals.alert({
        title: t("proformaDetail.refreshItemsBtn"),
        message:
          r.changed.length === 0
            ? t("proformaDetail.refreshItemsNone")
            : `${r.changed.length} ${t("proformaDetail.refreshItemsDoneSuf")}: ${r.changed
                .map(
                  (c) =>
                    `${c.plate ?? "?"} ${Number(c.from).toFixed(2)} → ${Number(c.to).toFixed(2)}`,
                )
                .join(" · ")}${r.resend ? `\n\n${t("proformaDetail.refreshItemsResend")}` : ""}`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformaDetail.error"),
        message: `${t("proformaDetail.error")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const removeMutation = trpc.proformaRequests.removeItem.useMutation({
    onSuccess: () => utils.proformaRequests.getById.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("proformaDetail.error"),
        message: `${t("proformaDetail.error")}: ${e.message}`,
        tone: "danger",
      }),
  });

  // F-roles — só edita itens quem é DRAFT E tem permissão de escrita.
  const canMutate = isDraft && canEdit;
  // F-proforma-split (CEO 2026-09-27) — técnico com intermediário: o item soma
  // as duas partes; a tela discrimina carro a carro.
  const split = items.some((it) => Number(it.intermediaryAmountSnapshot ?? 0) > 0);
  const totTec = items.reduce((s, it) => s + Number(it.technicianAmountSnapshot ?? 0), 0);
  const totInt = items.reduce((s, it) => s + Number(it.intermediaryAmountSnapshot ?? 0), 0);

  return (
    <div className="space-y-3">
      {canEdit && (canMutate || canRefresh) && (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => refreshMutation.mutate({ companyId, proformaRequestId })}
            disabled={refreshMutation.isPending}
            title={t("proformaDetail.refreshItemsHint")}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:border-neutral-500 disabled:opacity-40"
          >
            ↻ {t("proformaDetail.refreshItemsBtn")}
          </button>
          {canMutate && (
            <button
              onClick={onOpenAdd}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-3 py-1.5 text-sm"
            >
              {t("proformaDetail.addItemBtn")}
            </button>
          )}
        </div>
      )}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-x-auto">
        {items.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("proformaDetail.noItemsYet")}</p>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2 w-28">{t("proformaDetail.colInvoice")}</th>
                <th className="text-left px-3 py-2">{t("proformaDetail.colCustomerVehicle")}</th>
                <th className="text-left px-3 py-2">{t("proformaDetail.colTechnician")}</th>
                <th className="text-left px-3 py-2 w-24">{t("proformaDetail.colDate")}</th>
                <th
                  className="text-right px-3 py-2 w-28 font-mono"
                  title={t("proformaDetail.colInvoicedTitle")}
                >
                  {t("proformaDetail.colInvoiced")}
                </th>
                {split && (
                  <>
                    <th className="text-right px-3 py-2 w-28 font-mono">
                      {t("proformaDetail.colTechShare")}
                    </th>
                    <th className="text-right px-3 py-2 w-28 font-mono">
                      {t("proformaDetail.colIntermShare")}
                    </th>
                  </>
                )}
                <th className="text-right px-3 py-2 w-28 font-mono">
                  {t("proformaDetail.colGross")}
                </th>
                <th className="text-right px-3 py-2 w-20 font-mono">%</th>
                <th className="text-right px-3 py-2 w-28 font-mono">
                  {t("proformaDetail.colNet")}
                </th>
                <th
                  className="text-right px-3 py-2 w-28 font-mono"
                  title={t("proformaDetail.colMarginTitle")}
                >
                  {t("proformaDetail.colMargin")}
                </th>
                {canMutate && <th className="w-20" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {items.map((it) => (
                <tr key={it.id}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {/* F-proforma-item-fatura — mostra o nº da FATURA do cliente
                        (base de tudo); código do dossier vira subtítulo p/ rastreio.
                        O link continua abrindo o dossier. */}
                    <Link
                      href={`/dossiers/${it.vehicleServiceJobId}`}
                      className="text-lime hover:text-lime"
                    >
                      {it.invoiceNumberExternal ?? it.dossierCode ?? `#${it.vehicleServiceJobId}`}
                    </Link>
                    {it.invoiceNumberExternal && it.dossierCode && (
                      <div className="text-[10px] text-neutral-500">{it.dossierCode}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div>{it.customerNameSnapshot ?? "—"}</div>
                    <div className="text-xs text-neutral-500 font-mono">
                      {it.vehiclePlateSnapshot ?? "—"} · {it.vehicleMakeModelSnapshot ?? "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm">{it.technicianNameSnapshot}</td>
                  <td className="px-3 py-2 text-xs text-neutral-400">
                    {it.serviceDateSnapshot ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-neutral-400">
                    {formatMoney(it.faturadoCliente ?? 0, { decimals: 2 })}
                  </td>
                  {split && (
                    <>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {it.technicianAmountSnapshot != null
                          ? formatMoney(it.technicianAmountSnapshot, { decimals: 2 })
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {it.intermediaryAmountSnapshot != null
                          ? formatMoney(it.intermediaryAmountSnapshot, { decimals: 2 })
                          : "—"}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-neutral-400">
                    {formatMoney(it.grossAmountSnapshot, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap text-neutral-400">
                    {Number(it.retentionPercentSnapshot)}%
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                    {formatMoney(it.netAmountSnapshot, { decimals: 2 })}
                  </td>
                  {(() => {
                    const fat = Number(it.faturadoCliente ?? 0);
                    const m = fat - Number(it.netAmountSnapshot);
                    const ruim = fat > 0 && m <= 0.01;
                    return (
                      <td
                        className="px-3 py-2 text-right font-mono whitespace-nowrap"
                        style={{ color: ruim ? "#DC2626" : "#16A34A" }}
                      >
                        {ruim && "⚠ "}
                        {formatMoney(m, { decimals: 2 })}
                      </td>
                    );
                  })()}
                  {canMutate && (
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={async () => {
                          if (
                            !(await modals.confirm({
                              title: t("proformaDetail.confirm"),
                              message: `${t("proformaDetail.removeConfirmPre")} ${it.invoiceNumberExternal ?? it.dossierCode ?? `${t("proformaDetail.dossierWord")} #${it.vehicleServiceJobId}`}?`,
                            }))
                          )
                            return;
                          removeMutation.mutate({
                            companyId,
                            proformaRequestId,
                            itemId: it.id,
                          });
                        }}
                        disabled={removeMutation.isPending}
                        className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs"
                      >
                        {t("proformaDetail.removeBtn")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {split && (
              <tfoot className="border-t border-neutral-700 text-sm">
                <tr>
                  <td colSpan={5} className="px-3 py-2 text-right text-xs text-neutral-500">
                    {t("proformaDetail.splitTotalsLabel")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-semibold">
                    {formatMoney(totTec, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-semibold">
                    {formatMoney(totInt, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-mono whitespace-nowrap font-semibold">
                    {formatMoney(totTec + totInt, { decimals: 2 })}
                  </td>
                  <td colSpan={canMutate ? 4 : 3} />
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}

function OtherServicesTab({
  otherServices,
}: {
  otherServices: Array<{ id: number; description: string; amount: string; currency: string }>;
}) {
  const t = useT();
  if (otherServices.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <p className="text-sm text-neutral-500">{t("proformaDetail.noOtherServices")}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="text-left px-3 py-2">{t("proformaDetail.colDescription")}</th>
            <th className="text-right px-3 py-2 w-28 font-mono">{t("proformaDetail.colAmount")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          {otherServices.map((s) => (
            <tr key={s.id}>
              <td className="px-3 py-2">{s.description}</td>
              <td className="px-3 py-2 text-right font-mono">
                {formatMoney(s.amount, { decimals: 2 })} {s.currency}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({
  companyId,
  proformaRequestId,
  isAdmin,
}: {
  companyId: number;
  proformaRequestId: number;
  isAdmin: boolean;
}) {
  const t = useT();
  // audit.listByCompany requer role admin. Usuários não-admin recebem mensagem.
  const auditQuery = trpc.audit.listByCompany.useQuery(
    {
      companyId,
      entityKind: "proforma_request",
      entityId: proformaRequestId,
      limit: 50,
      offset: 0,
    },
    { enabled: isAdmin },
  );
  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <p className="text-sm text-neutral-500">
          {t("proformaDetail.auditAdminOnlyPre")}{" "}
          <Link href="/audit" className="text-lime hover:text-lime">
            /audit
          </Link>{" "}
          {t("proformaDetail.auditAdminOnlyPost")}
        </p>
      </div>
    );
  }
  if (auditQuery.isLoading) {
    return <p className="text-sm text-neutral-500 p-4">{t("proformaDetail.loadingAudit")}</p>;
  }
  const rows = auditQuery.data?.rows ?? [];
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
      {rows.length === 0 && (
        <p className="p-4 text-sm text-neutral-500">{t("proformaDetail.noAuditEntries")}</p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2 w-44">{t("proformaDetail.colDate")}</th>
              <th className="text-left px-3 py-2 w-44">{t("proformaDetail.colAction")}</th>
              <th className="text-left px-3 py-2">{t("proformaDetail.colReasonChanges")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-xs text-neutral-400">
                  {new Date(r.occurredAt).toLocaleString("pt-BR")}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                <td className="px-3 py-2 text-xs text-neutral-400">
                  {r.reason ??
                    (r.fieldChanges ? JSON.stringify(r.fieldChanges).slice(0, 200) : "—")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddItemModal({
  companyId,
  proformaRequestId,
  onClose,
}: {
  companyId: number;
  proformaRequestId: number;
  onClose: () => void;
}) {
  const [vehicleServiceJobId, setVehicleServiceJobId] = useState("");
  const [technicianId, setTechnicianId] = useState<number | null>(null);
  const [retentionPercent, setRetentionPercent] = useState("6");
  const [error, setError] = useState<string | null>(null);
  const tr = useT();

  const utils = trpc.useUtils();
  const techsQuery = trpc.technicians.list.useQuery({
    companyId,
    active: true,
    limit: 100,
    offset: 0,
  });
  const addMutation = trpc.proformaRequests.addItem.useMutation({
    onSuccess: () => {
      utils.proformaRequests.getById.invalidate();
      onClose();
    },
    onError: (e) => setError(e.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const jobId = Number(vehicleServiceJobId);
    if (!Number.isFinite(jobId) || jobId <= 0) {
      setError(tr("proformaDetail.errInvalidDossierId"));
      return;
    }
    if (!technicianId) {
      setError(tr("proformaDetail.errSelectTechnician"));
      return;
    }
    const pct = Number(retentionPercent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setError(tr("proformaDetail.errRetentionRange"));
      return;
    }
    addMutation.mutate({
      companyId,
      proformaRequestId,
      vehicleServiceJobId: jobId,
      technicianId,
      retentionPercent: pct,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-neutral-950 border border-neutral-800 rounded-lg p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-lg font-semibold">{tr("proformaDetail.addItemTitle")}</h2>
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {tr("proformaDetail.dossierIdLabel")}
          </label>
          <input
            type="number"
            value={vehicleServiceJobId}
            onChange={(e) => setVehicleServiceJobId(e.target.value)}
            required
            min={1}
            placeholder={tr("proformaDetail.dossierIdPlaceholder")}
            className={inputClass + " w-full"}
          />
          <p className="text-xs text-neutral-500 mt-1">{tr("proformaDetail.dossierIdHelp")}</p>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {tr("proformaDetail.technicianLabel")}
          </label>
          <select
            value={technicianId ?? ""}
            onChange={(e) => setTechnicianId(Number(e.target.value) || null)}
            required
            className={inputClass + " w-full"}
          >
            <option value="">{tr("proformaDetail.selectPlaceholder")}</option>
            {(techsQuery.data?.rows ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
            {tr("proformaDetail.retentionLabel")}
          </label>
          <input
            type="number"
            value={retentionPercent}
            onChange={(e) => setRetentionPercent(e.target.value)}
            min={0}
            max={100}
            step={0.01}
            className={inputClass + " w-full"}
          />
        </div>
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={addMutation.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {tr("proformaDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={addMutation.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
          >
            {addMutation.isPending ? tr("proformaDetail.adding") : tr("proformaDetail.add")}
          </button>
        </div>
      </form>
    </div>
  );
}

function TransitionModal({
  companyId,
  proformaRequestId,
  transition,
  recipientEmail,
  recipientName,
  recipientType,
  recipientTechnicianId,
  netTotal,
  ownMirrors,
  onClose,
}: {
  companyId: number;
  proformaRequestId: number;
  transition: TransitionMeta;
  recipientEmail: string | null;
  recipientName: string;
  recipientType: string;
  /** F-adiantamento-tecnico — destinatário técnico: carrega os adiantamentos abertos. */
  recipientTechnicianId: number | null;
  netTotal: number;
  /** F-cancel-removes-mirrors — espelhos ativos criados por esta proforma na empresa destino. */
  ownMirrors: { active: number; blocked: number } | null;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  // F-returned-invoice-kind (CEO 2026-09-30) — o tipo segue o destinatário:
  // proforma para empresa do grupo → fatura intercompany; técnico ou
  // intermediário externo → fatura de técnico. Antes vinha sempre
  // "intercompany" e a proforma do Luciano dava erro.
  const [returnedInvoiceKind, setReturnedInvoiceKind] = useState<"intercompany" | "technician">(
    recipientType === "intercompany" ? "intercompany" : "technician",
  );
  // F-proforma-attach-invoice — nº do doc do fornecedor (texto) + PDF anexo.
  const [returnedInvoiceNumber, setReturnedInvoiceNumber] = useState("");
  // F-adiantamento-tecnico (CEO 2026-10-25) — ao registrar a fatura recebida o
  // CEO marca quais adiantamentos o técnico descontou; a proforma passa a
  // valer líquido − adiantamentos e eles ficam "compensados".
  const advancesQuery = trpc.technicianAdvances.openForTechnician.useQuery(
    { companyId, technicianId: recipientTechnicianId ?? 0 },
    { enabled: transition.action === "receive_invoice" && recipientTechnicianId != null },
  );
  const [advanceIds, setAdvanceIds] = useState<number[]>([]);
  const advancesAll = advancesQuery.data ?? [];
  useEffect(() => {
    // Pré-marca todos: a regra da casa é descontar tudo na próxima fatura.
    setAdvanceIds(advancesAll.map((a) => a.id));
  }, [advancesAll.length]);
  const advancesSum = advancesAll
    .filter((a) => advanceIds.includes(a.id))
    .reduce((s, a) => s + Number(a.amount), 0);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  // F-proforma-send-email-default — quando há recipientEmail, default true: ao
  // marcar como "enviada", já dispara o email com PDF anexo. Usuário pode
  // desmarcar para fazer só a transição administrativa (caso queira editar
  // emailSubject/customMessage antes de mandar, ou enviar manualmente depois).
  const [sendEmailOnTransition, setSendEmailOnTransition] = useState(Boolean(recipientEmail));
  // F-proforma-send-to (CEO 2026-09-28) — desmarcar o cadastrado e/ou mandar
  // para outro(s) e-mail(s). Vale só para este envio.
  const [useRegistered, setUseRegistered] = useState(Boolean(recipientEmail));
  const [otherEmails, setOtherEmails] = useState("");
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const modals = useModals();
  const t = useT();
  function onSuccess() {
    // F-bs-mirror-navfix (2026-05-31) — invalida TODO o router proformaRequests
    // (não só getById/list) para que `mirrorStatus` refaça após a transição.
    // Antes só getById+list eram invalidados: o status mudava mas o botão
    // "Importar pra Brazilian Storms" não aparecia (mirrorStatus ficava com o
    // valor antigo, eligible=false de quando era draft) até um reload manual.
    utils.proformaRequests.invalidate();
    onClose();
  }
  function onError(e: { message: string }) {
    setError(e.message);
  }
  const opts = { onSuccess, onError };
  // F-proforma-send-email-default — send tem onSuccess customizado que avisa
  // se email saiu junto (depende do checkbox + recipientEmail).
  const sendMut = trpc.proformaRequests.send.useMutation({
    onSuccess: (res) => {
      // F-bs-mirror-navfix — invalida o router inteiro (inclui mirrorStatus) para
      // o botão "Importar pra Brazilian Storms" aparecer já após o envio.
      utils.proformaRequests.invalidate();
      if (res.emailFailed && res.emailFailed.length > 0) {
        void modals.alert({
          title: t("proformaDetail.error"),
          message: `${t("proformaDetail.emailFailedPre")} ${res.emailFailed.join(", ")}${
            res.emailSentTo?.length
              ? ` · ${t("proformaDetail.sentWithEmailPre")} ${res.emailSentTo.join(", ")}`
              : ""
          }`,
          tone: "danger",
        });
      } else if (res.emailSentTo && res.emailSentTo.length > 0) {
        void modals.alert({
          message: `${t("proformaDetail.sentWithEmailPre")} ${res.emailSentTo.join(", ")}. ${t("proformaDetail.sentWithEmailPost")}`,
        });
      } else {
        void modals.alert({
          message: t("proformaDetail.sentNoEmail"),
        });
      }
      onClose();
    },
    onError,
  });
  const approveMut = trpc.proformaRequests.approve.useMutation(opts);
  const rejectMut = trpc.proformaRequests.reject.useMutation(opts);
  const reopenMut = trpc.proformaRequests.reopen.useMutation(opts);
  // F-proforma-attach-invoice — após registrar a invoice, faz upload do PDF
  // (se anexado) via POST /uploads antes de fechar o modal.
  const receiveInvoiceMut = trpc.proformaRequests.receiveInvoice.useMutation({
    onSuccess: async () => {
      if (invoiceFile) {
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", invoiceFile);
          fd.append("companyId", String(companyId));
          fd.append("ownerId", String(proformaRequestId));
          fd.append("ownerKind", "proforma_returned_invoice");
          // URL relativa — vai pelo proxy do Vite em dev (e mesmo-origin em
          // prod), igual ao tRPC em "/trpc". Antes usava ${VITE_API_URL}, que
          // não era setado → o fetch ia pra "undefined/uploads" e falhava.
          const resp = await fetch(`/uploads`, {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          if (!resp.ok) {
            const msg = await resp.text();
            void modals.alert({
              title: t("proformaDetail.error"),
              message:
                `${t("proformaDetail.invoiceSavedUploadFailedPre")} (${resp.status}): ${msg}. ` +
                t("proformaDetail.canReattachLater"),
              tone: "danger",
            });
          }
        } catch (err) {
          void modals.alert({
            title: t("proformaDetail.error"),
            message: `${t("proformaDetail.invoiceSavedUploadFailedPre")}: ${
              err instanceof Error ? err.message : String(err)
            }. ${t("proformaDetail.canReattachLater")}`,
            tone: "danger",
          });
        } finally {
          setUploading(false);
        }
      }
      // F-bs-mirror-navfix — router inteiro (inclui mirrorStatus) + anexos.
      utils.proformaRequests.invalidate();
      utils.attachments.listByOwner.invalidate();
      onClose();
    },
    onError,
  });
  const markPaidMut = trpc.proformaRequests.markPaid.useMutation(opts);
  const closeMut = trpc.proformaRequests.close.useMutation(opts);
  const cancelMut = trpc.proformaRequests.cancel.useMutation(opts);

  const pending =
    sendMut.isPending ||
    approveMut.isPending ||
    rejectMut.isPending ||
    reopenMut.isPending ||
    receiveInvoiceMut.isPending ||
    uploading ||
    markPaidMut.isPending ||
    closeMut.isPending ||
    cancelMut.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (transition.requiresReason && reason.trim().length === 0) {
      setError(t("proformaDetail.errReasonRequired"));
      return;
    }
    const base = { companyId, proformaRequestId };
    switch (transition.action) {
      case "send": {
        if (!sendEmailOnTransition) {
          sendMut.mutate({ ...base, sendEmailOnTransition: false });
          break;
        }
        const outros = otherEmails
          .split(/[\s,;]+/)
          .map((x) => x.trim().toLowerCase())
          .filter(Boolean);
        const invalido = outros.find((x) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
        if (invalido) {
          setError(`${t("proformaDetail.errInvalidEmail")} ${invalido}`);
          return;
        }
        const destinos = [
          ...new Set([
            ...(useRegistered && recipientEmail ? [recipientEmail.toLowerCase()] : []),
            ...outros,
          ]),
        ];
        if (destinos.length === 0) {
          setError(t("proformaDetail.errNoRecipient"));
          return;
        }
        if (destinos.length > 5) {
          setError(t("proformaDetail.errTooManyRecipients"));
          return;
        }
        sendMut.mutate({ ...base, sendEmailOnTransition: true, emailRecipients: destinos });
        break;
      }
      case "approve":
        approveMut.mutate(base);
        break;
      case "reject":
        rejectMut.mutate({ ...base, reason });
        break;
      case "reopen":
        reopenMut.mutate(base);
        break;
      case "receive_invoice": {
        const num = returnedInvoiceNumber.trim();
        if (num.length === 0) {
          setError(t("proformaDetail.errSupplierInvoiceNumberRequired"));
          return;
        }
        receiveInvoiceMut.mutate({
          ...base,
          returnedInvoiceKind,
          returnedInvoiceNumber: num,
          ...(advanceIds.length > 0 ? { advanceIds } : {}),
        });
        break;
      }
      case "mark_paid":
        markPaidMut.mutate(base);
        break;
      case "close":
        closeMut.mutate(base);
        break;
      case "cancel":
        cancelMut.mutate({ ...base, reason });
        break;
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-neutral-950 border border-neutral-800 rounded-lg p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-lg font-semibold">{transition.label}</h2>

        {/* F-proforma-send-email-default — bloco específico do "send":
            mostra destinatário e checkbox para também disparar email. */}
        {transition.action === "send" && (
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmailOnTransition}
                onChange={(e) => setSendEmailOnTransition(e.target.checked)}
                className="mt-0.5 accent-lime"
              />
              <span>
                {t("proformaDetail.alsoSendEmail")}{" "}
                <span className="text-neutral-500">{t("proformaDetail.pdfAttachedNote")}</span>
              </span>
            </label>
            {sendEmailOnTransition && (
              <div className="space-y-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">
                  {t("proformaDetail.sendToLabel")}
                </div>
                {recipientEmail && (
                  <label
                    className="flex items-start gap-2 cursor-pointer"
                    data-testid="send-registered-email"
                  >
                    <input
                      type="checkbox"
                      checked={useRegistered}
                      onChange={(e) => setUseRegistered(e.target.checked)}
                      className="mt-1 accent-lime"
                    />
                    <span className={useRegistered ? "" : "line-through opacity-50"}>
                      <span className="font-mono break-all">{recipientEmail}</span>
                      <span className="block text-xs text-neutral-400">
                        {recipientName} · {t("proformaDetail.registeredEmailTag")}
                      </span>
                    </span>
                  </label>
                )}
                <div>
                  <label className="mb-1 block text-xs text-neutral-400">
                    {t("proformaDetail.otherEmailLabel")}
                  </label>
                  <input
                    type="text"
                    inputMode="email"
                    value={otherEmails}
                    onChange={(e) => setOtherEmails(e.target.value)}
                    placeholder="nome@exemplo.com"
                    className={inputClass + " w-full font-mono"}
                    data-testid="send-other-email"
                  />
                  <p className="mt-1 text-[11px] text-neutral-500">
                    {t("proformaDetail.otherEmailHint")}
                  </p>
                </div>
              </div>
            )}
            {/* F-proforma-sem-email (CEO 2026-09-30, dupla J&J): o aviso dizia
                "nenhum e-mail será enviado" — mas dá para mandar a OUTRO
                endereço. Agora ele orienta e some quando há e-mail digitado. */}
            {!recipientEmail && !(sendEmailOnTransition && otherEmails.trim() !== "") && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                {t("proformaDetail.noEmailOtherPre")}{" "}
                <b>
                  {recipientType === "technician"
                    ? t("proformaDetail.techniciansBold")
                    : t("proformaDetail.companiesBold")}
                </b>
                .
              </div>
            )}
          </div>
        )}

        {transition.action === "receive_invoice" && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {t("proformaDetail.returnedInvoiceTypeLabel")}
              </label>
              <select
                value={returnedInvoiceKind}
                onChange={(e) =>
                  setReturnedInvoiceKind(e.target.value as "intercompany" | "technician")
                }
                className={inputClass + " w-full"}
              >
                {recipientType === "intercompany" ? (
                  <option value="intercompany">{t("proformaDetail.optIntercompanyInvoice")}</option>
                ) : (
                  <option value="technician">{t("proformaDetail.optTechnicianInvoice")}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {t("proformaDetail.supplierInvoiceNumberLabel")}{" "}
                <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={returnedInvoiceNumber}
                onChange={(e) => setReturnedInvoiceNumber(e.target.value)}
                required
                maxLength={64}
                placeholder={t("proformaDetail.supplierInvoiceNumberPlaceholder")}
                className={inputClass + " w-full"}
              />
              <p className="text-[11px] text-neutral-500 mt-1">
                {t("proformaDetail.supplierInvoiceNumberHelp")}
              </p>
            </div>
            {advancesAll.length > 0 && (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"
                data-testid="advances-checklist"
              >
                <p className="text-xs font-semibold text-amber-300">
                  {t("proformaDetail.advancesTitle")}
                </p>
                <p className="mb-2 text-[11px] text-neutral-400">
                  {t("proformaDetail.advancesHint")}
                </p>
                <div className="space-y-1">
                  {advancesAll.map((a) => (
                    <label
                      key={a.id}
                      className="flex cursor-pointer items-center gap-2 text-xs"
                      data-testid={`advance-${a.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={advanceIds.includes(a.id)}
                        onChange={(e) =>
                          setAdvanceIds((prev) =>
                            e.target.checked ? [...prev, a.id] : prev.filter((id) => id !== a.id),
                          )
                        }
                      />
                      <span className="font-mono text-neutral-400">{a.occurredAt}</span>
                      <span className="flex-1 truncate">{a.description}</span>
                      <span className="font-mono tabular-nums">
                        {formatMoney(a.amount, { decimals: 2 })}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs" data-testid="advances-sum">
                  {t("proformaDetail.advancesExpected")}{" "}
                  <span className="font-mono font-semibold tabular-nums">
                    {formatMoney(Math.max(0, netTotal - advancesSum), { decimals: 2 })}
                  </span>
                  <span className="text-neutral-500">
                    {" "}
                    ({formatMoney(netTotal, { decimals: 2 })} −{" "}
                    {formatMoney(advancesSum, { decimals: 2 })})
                  </span>
                </p>
              </div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {t("proformaDetail.attachInvoicePdfLabel")}
              </label>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
                className="block w-full text-xs text-neutral-400 file:mr-3 file:rounded-md file:border-0 file:bg-neutral-800 file:px-3 file:py-1.5 file:text-neutral-200 hover:file:bg-neutral-700"
              />
              {invoiceFile && (
                <p className="text-[11px] text-lime mt-1">
                  ✓ {invoiceFile.name} ({(invoiceFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
          </>
        )}

        {transition.action === "cancel" && ownMirrors != null && ownMirrors.active > 0 && (
          <div
            className={
              ownMirrors.blocked > 0
                ? "text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                : "text-amber-200 text-sm bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2"
            }
          >
            {ownMirrors.blocked > 0
              ? `${t("proformaDetail.cancelMirrorsBlockedPre")} ${ownMirrors.blocked} ${t("proformaDetail.cancelMirrorsBlockedMid")} ${ownMirrors.active} ${t("proformaDetail.cancelMirrorsBlockedSuf")}`
              : `${t("proformaDetail.cancelMirrorsPre")} ${ownMirrors.active} ${t("proformaDetail.cancelMirrorsSuf")}`}
          </div>
        )}

        {transition.requiresReason && (
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {t("proformaDetail.reasonLabel")}{" "}
              {transition.requiresReason && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              maxLength={500}
              rows={3}
              placeholder={t("proformaDetail.reasonPlaceholder")}
              className={inputClass + " w-full resize-y"}
            />
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("proformaDetail.cancel")}
          </button>
          <button
            type="submit"
            disabled={pending}
            className={
              transition.tone === "danger"
                ? "rounded-md border border-red-500 bg-red-500/20 hover:bg-red-500/30 text-red-200 disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
                : "rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
            }
          >
            {uploading
              ? t("proformaDetail.uploadingAttachment")
              : pending
                ? t("proformaDetail.processing")
                : t("proformaDetail.confirm")}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}

/** CEO 2026-10-26 — selo do pagamento registrado por fora + "Desfazer" (admin). */
function ExternalPaymentInfo({
  companyId,
  proformaRequestId,
  method,
  note,
  paidAt,
  cancelled,
  cancelledReason,
  isAdmin,
}: {
  companyId: number;
  proformaRequestId: number;
  method: string;
  note: string | null;
  paidAt: string | Date | null;
  cancelled: boolean;
  cancelledReason: string | null;
  isAdmin: boolean;
}) {
  const tr = useT();
  const utils = trpc.useUtils();
  const [desfazendo, setDesfazendo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const undo = trpc.proformaRequests.undoExternalPayment.useMutation({
    onSuccess: () => {
      setDesfazendo(false);
      void utils.proformaRequests.invalidate();
    },
    onError: (e) => setErro(e.message),
  });
  const rotulo: Record<string, string> = {
    salario: tr("proformaDetail.extMethodSalario"),
    dinheiro: tr("proformaDetail.extMethodDinheiro"),
    transferencia: tr("proformaDetail.extMethodTransferencia"),
    outro: tr("proformaDetail.extMethodOutro"),
  };
  const data = paidAt ? new Date(paidAt).toLocaleDateString("fr-FR") : "—";
  return (
    <div
      className="rounded-md border border-neutral-800 px-3 py-2 text-sm space-y-1"
      data-testid="external-payment-info"
    >
      <p className="flex flex-wrap items-center gap-2">
        <span className={`badge ${cancelled ? "b-mut" : "b-violet"}`}>
          {cancelled ? tr("proformaDetail.extUndoneTitle") : tr("proformaDetail.extTitle")}
        </span>
        <span className="text-neutral-300">
          {rotulo[method] ?? method} · {data}
        </span>
      </p>
      {note && <p className="text-xs text-neutral-300">{note}</p>}
      {cancelled && cancelledReason && (
        <p className="text-xs text-neutral-500">
          {tr("proformaDetail.extUndoneReason")}: {cancelledReason}
        </p>
      )}
      {!cancelled && <p className="text-[11px] text-neutral-500">{tr("proformaDetail.extHint")}</p>}
      {!cancelled && isAdmin && !desfazendo && (
        <button
          type="button"
          onClick={() => setDesfazendo(true)}
          className="rounded-md border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:border-red-500"
          data-testid="external-payment-undo"
        >
          {tr("proformaDetail.extUndoBtn")}
        </button>
      )}
      {desfazendo && (
        <div className="space-y-2 pt-1">
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={tr("proformaDetail.extUndoReasonPh")}
            className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm"
          />
          {erro && <p className="text-xs text-red-400">{erro}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={motivo.trim().length < 5 || undo.isPending}
              onClick={() => undo.mutate({ companyId, proformaRequestId, reason: motivo.trim() })}
              className="rounded-md bg-red-600 px-3 py-1 text-xs text-white disabled:opacity-40"
              data-testid="external-payment-undo-confirm"
            >
              {tr("proformaDetail.extUndoConfirm")}
            </button>
            <button
              type="button"
              onClick={() => setDesfazendo(false)}
              className="rounded-md border border-neutral-700 px-3 py-1 text-xs"
            >
              {tr("proformaDetail.extUndoCancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
